import { useState, useRef, useMemo } from 'react'
import { motion } from 'motion/react'
import { useDrag } from '@use-gesture/react'
import { useMotionValue } from 'motion/react'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import 'react-photo-view/dist/react-photo-view.css'
import { ArrowLeft } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { useTelegram, useFullscreen } from '@/shared/lib/telegram'
import {
  getOptimizedImageSizes,
  getOptimizedImageSrcSet,
  getOptimizedImageUrl,
  preloadOptimizedImage,
} from '@/shared/lib/image-optimization'

export interface ProductHeroGalleryProps {
  images: string[]
  initialIndex?: number
  onIndexChange?: (index: number) => void
  height?: number
}

export function ProductHeroGallery({
  images,
  initialIndex = 0,
  onIndexChange,
  height = 65,
}: ProductHeroGalleryProps) {
  const { hapticFeedback, webApp } = useTelegram()
  const { requestFullscreen, exitFullscreen } = useFullscreen()
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const closeLightboxRef = useRef<(() => void) | null>(null)
  const x = useMotionValue(0)

  const displayImages = useMemo(
    () => (!images || images.length === 0) ? ['/placeholder-product.svg'] : images,
    [images],
  )

  const handleIndexChange = (newIndex: number) => {
    const clamped = Math.max(0, Math.min(newIndex, displayImages.length - 1))
    setCurrentIndex(clamped)
    onIndexChange?.(clamped)
    hapticFeedback.selectionChanged()
    // Preload adjacent images
    preloadOptimizedImage(displayImages[clamped + 1], { preset: 'hero' })
    preloadOptimizedImage(displayImages[clamped - 1], { preset: 'hero' })
  }

  const bindSwipe = useDrag(
    ({ movement: [mx], direction: [dx], velocity, last }) => {
      if (last) {
        if (Math.abs(mx) > 50 || Math.abs(velocity[0]) > 0.5) {
          if (dx < 0 && currentIndex < displayImages.length - 1) handleIndexChange(currentIndex + 1)
          else if (dx > 0 && currentIndex > 0) handleIndexChange(currentIndex - 1)
        }
        x.set(0)
      } else {
        x.set(mx)
      }
    },
    { axis: 'x', filterTaps: true, rubberband: true },
  )

  // Cap height on desktop so wide monitors don't get 65vh of stretched image
  const galleryStyle = { height: `min(${height}vh, 520px)` }

  return (
    <PhotoProvider
      speed={() => 200}
      easing={(type) => (type === 2 ? 'cubic-bezier(0.36, 0, 0.66, -0.56)' : 'cubic-bezier(0.34, 1.56, 0.64, 1)')}
      onIndexChange={(index) => {
        setCurrentIndex(index)
        onIndexChange?.(index)
        hapticFeedback.selectionChanged()
      }}
      onVisibleChange={(visible) => {
        if (visible) {
          webApp?.disableVerticalSwipes?.()
          requestFullscreen()
        } else {
          webApp?.enableVerticalSwipes?.()
          exitFullscreen()
        }
      }}
      maskOpacity={0.98}
      bannerVisible={false}
      overlayRender={({ index, onClose }) => {
        // Store close fn for VK back button
        closeLightboxRef.current = onClose
        return (
          <>
            <button
              onClick={onClose}
              className="fixed top-4 left-4 z-[1001] w-10 h-10 flex items-center justify-center rounded-full bg-white/15 backdrop-blur-sm text-white hover:bg-white/25 transition-colors active:scale-95"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            {displayImages.length > 1 && (
              <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[1001] px-4 py-2 rounded-full bg-white/15 backdrop-blur-sm text-white text-sm pointer-events-none select-none">
                {index + 1} / {displayImages.length}
              </div>
            )}
          </>
        )
      }}
    >
      {/*
       * All images registered in order so lightbox swipe works across all photos.
       * Only the active image is rendered visibly; others are hidden spans but
       * still occupy a slot in PhotoProvider's internal registry.
       */}
      <div
        className="relative w-full overflow-hidden bg-black touch-pan-y"
        style={galleryStyle}
        {...bindSwipe()}
      >
        {displayImages.map((img, i) => {
          const fullSrc = getOptimizedImageUrl(img, { preset: 'full' })
          const heroSrc = getOptimizedImageUrl(img, { preset: 'hero' })
          const heroSrcSet = getOptimizedImageSrcSet(img, { preset: 'hero' })
          const isActive = i === currentIndex

          return (
            <PhotoView key={i} src={fullSrc}>
              {isActive ? (
                <motion.div
                  className="absolute inset-0"
                  key={i}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.22, ease: 'easeOut' }}
                  style={{ pointerEvents: 'auto' }}
                >
                  <img
                    src={heroSrc}
                    srcSet={heroSrcSet}
                    sizes={getOptimizedImageSizes('hero')}
                    alt={`Изображение ${i + 1}`}
                    className="w-full h-full object-cover select-none cursor-pointer bg-[#EDE5D6] dark:bg-[#231712]"
                    draggable={false}
                    loading="eager"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement
                      if (!target.src.endsWith('/placeholder-product.svg')) {
                        target.src = '/placeholder-product.svg'
                      }
                    }}
                  />
                </motion.div>
              ) : (
                // Hidden span keeps the slot in PhotoProvider's registry
                <span style={{ display: 'none' }} />
              )}
            </PhotoView>
          )
        })}

        {displayImages.length > 1 && (
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-10 pointer-events-none">
            {displayImages.map((_, index) => (
              <div
                key={index}
                className={cn(
                  'h-1.5 rounded-full transition-all duration-200',
                  index === currentIndex ? 'w-6 bg-white' : 'w-1.5 bg-white/50',
                )}
              />
            ))}
          </div>
        )}
      </div>
    </PhotoProvider>
  )
}
